import {
  type NotificationRenderContext,
  renderNotification,
  type StorefrontTheme,
} from "@loot-os/api-contract";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@loot-os/ui/components/alert-dialog";
import { Badge } from "@loot-os/ui/components/badge";
import { Button } from "@loot-os/ui/components/button";
import {
  Field,
  FieldDescription,
  FieldError,
  FieldLabel,
  FieldLegend,
  FieldSet,
} from "@loot-os/ui/components/field";
import { Input } from "@loot-os/ui/components/input";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@loot-os/ui/components/sheet";
import { toast } from "@loot-os/ui/components/sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@loot-os/ui/components/tabs";
import { Textarea } from "@loot-os/ui/components/textarea";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CircleCheck } from "lucide-react";
import { useMemo, useState } from "react";

import { useSessionQuery } from "../lib/authQueries";
import { formatDate } from "../lib/format";
import {
  defaultSchedule,
  isoToLocalSchedule,
  type LocalSchedule,
  localScheduleToIso,
} from "../lib/localSchedule";
import {
  campaignAudiencePhrase,
  campaignSavedMessage,
  draftFromContent,
  draftToContent,
  EMPTY_NOTIFICATION_DRAFT,
  hasNoIssues,
  isCampaignDue,
  type NotificationCampaign,
  notificationDraftIssues,
  parseRecipientList,
  RECIPIENT_LIST_MAX,
  scheduleIssue,
} from "../lib/notifications";
import { useTRPC } from "../lib/trpc";
import { DayTimeFields } from "./DayTimeFields";
import { DeleteNotificationCampaignButton } from "./DeleteNotificationCampaignButton";
import { NotificationContentFields } from "./NotificationContentFields";
import { NotificationPreview } from "./NotificationPreview";

type Audience = NotificationCampaign["audience"];

/** The biggest list file read in the browser; ten thousand emails are well under it. */
const MAX_LIST_FILE_BYTES = 2 * 1024 * 1024;

/** How many unmatched entries the check lists before it counts the rest. */
const UNMATCHED_SHOWN = 50;

/**
 * The marketing composer, in a sheet: content, audience, when, a live preview and a test. A new
 * one or a scheduled one is editable; a sent one opens read-only.
 */
export function NotificationCampaignSheet({
  editorKey,
  campaign,
  open,
  onOpenChange,
  context,
  theme,
  uploadsAvailable,
}: {
  /** Changes on every open, so each opens on a fresh draft; null before the first. */
  editorKey: number | null;
  /** The campaign opened, or null for a new one. */
  campaign: NotificationCampaign | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  context: NotificationRenderContext;
  theme?: StorefrontTheme;
  uploadsAvailable: boolean;
}) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="gap-0 overflow-y-auto data-[side=right]:w-full data-[side=right]:sm:max-w-4xl">
        {editorKey !== null && (
          <CampaignEditor
            key={editorKey}
            campaign={campaign}
            context={context}
            theme={theme}
            uploadsAvailable={uploadsAvailable}
            onDone={() => onOpenChange(false)}
          />
        )}
      </SheetContent>
    </Sheet>
  );
}

function CampaignEditor({
  campaign,
  context,
  theme,
  uploadsAvailable,
  onDone,
}: {
  campaign: NotificationCampaign | null;
  context: NotificationRenderContext;
  theme?: StorefrontTheme;
  uploadsAvailable: boolean;
  onDone: () => void;
}) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const session = useSessionQuery();
  const readOnly = campaign?.status === "sent";
  const [now] = useState(() => new Date());
  const [draft, setDraft] = useState(() =>
    campaign ? draftFromContent(campaign.content) : EMPTY_NOTIFICATION_DRAFT,
  );
  const [audience, setAudience] = useState<Audience>(campaign?.audience ?? "all");
  const [listText, setListText] = useState("");
  // A campaign already due (saved to go now, not yet sent) reopens as one to send now.
  const scheduledLater = campaign !== null && !isCampaignDue(campaign, now);
  const [when, setWhen] = useState<"now" | "later">(scheduledLater ? "later" : "now");
  const [schedule, setSchedule] = useState<LocalSchedule>(() =>
    campaign && scheduledLater ? isoToLocalSchedule(campaign.scheduledAt) : defaultSchedule(now),
  );
  const [attempted, setAttempted] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const list = useMemo(() => parseRecipientList(listText), [listText]);
  // Editing a list campaign with the box left empty keeps the list it was saved with.
  const keepsList = campaign?.audience === "list" && list.identifiers.length === 0;
  const resolve = useMutation(trpc.notifications.resolveRecipients.mutationOptions());
  // A check answers for the list it was sent; an edit since makes it stale.
  const checked =
    resolve.data && resolve.variables?.identifiers.join("\n") === list.identifiers.join("\n")
      ? resolve.data
      : null;

  const onSaved = (
    { campaign: saved, unmatched }: { campaign: NotificationCampaign; unmatched: string[] },
    sentNow: boolean,
  ) => {
    void queryClient.invalidateQueries({ queryKey: trpc.notifications.campaigns.pathKey() });
    toast.success(
      campaignSavedMessage({
        campaign: saved,
        sentNow,
        scheduledFor: formatDate(saved.scheduledAt),
        unmatched: unmatched.length,
      }),
    );
    onDone();
  };
  const create = useMutation(trpc.notifications.createCampaign.mutationOptions());
  const update = useMutation(trpc.notifications.updateCampaign.mutationOptions());
  const test = useMutation(
    trpc.notifications.sendCampaignTest.mutationOptions({
      onSuccess: () => toast.success("Test sent. Open the storefront's bell to see it."),
    }),
  );

  const content = draftToContent(draft);
  const issues = notificationDraftIssues(draft);
  const rendered = renderNotification({ kind: "marketing", content }, context);
  const listIssue =
    audience !== "list" || keepsList
      ? null
      : list.identifiers.length === 0
        ? "Add at least one email, id or external id."
        : list.identifiers.length > RECIPIENT_LIST_MAX
          ? `A list may name at most ${RECIPIENT_LIST_MAX.toLocaleString("en-US")} people.`
          : checked?.matched === 0
            ? "No one on the list matches a shopper in this environment."
            : null;
  const timeIssue = when === "later" ? scheduleIssue(schedule, new Date()) : null;
  const valid = hasNoIssues(issues) && !listIssue && !timeIssue;
  const saving = create.isPending || update.isPending;

  const persist = () => {
    const scheduledAt = when === "now" ? null : localScheduleToIso(schedule);
    const input = {
      content,
      audience,
      identifiers: audience === "list" && !keepsList ? list.identifiers : undefined,
      scheduledAt,
    };
    const options = {
      onSuccess: (result: Parameters<typeof onSaved>[0]) => onSaved(result, scheduledAt === null),
    };
    if (campaign) update.mutate({ ...input, campaignId: campaign.id }, options);
    else create.mutate(input, options);
  };

  const submit = () => {
    setAttempted(true);
    if (!valid) return;
    // Sending can't be taken back, so it asks first; a schedule can still be changed.
    if (when === "now") setConfirming(true);
    else persist();
  };

  const sendTest = () => {
    setAttempted(true);
    if (hasNoIssues(issues)) test.mutate({ content });
  };

  const readList = async (file: File | undefined) => {
    if (!file) return;
    if (file.size > MAX_LIST_FILE_BYTES) {
      toast.error("That file is larger than 2 MB. Split the list, or paste it.");
      return;
    }
    setListText(await file.text());
  };

  const title = readOnly
    ? rendered.title
    : campaign
      ? "Edit scheduled notification"
      : "New notification";

  return (
    <form
      className="flex min-h-full flex-col"
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
    >
      <SheetHeader className="border-b pr-12">
        <div className="flex flex-wrap items-center gap-2">
          <SheetTitle className="text-base">{title}</SheetTitle>
          {campaign && (
            <Badge variant={campaign.status === "sent" ? "secondary" : "outline"}>
              {campaign.status}
            </Badge>
          )}
        </div>
        <SheetDescription>
          {readOnly && campaign
            ? `Sent ${formatDate(campaign.sentAt)} to ${campaignAudiencePhrase(campaign)}; delivered to ${(campaign.deliveredCount ?? 0).toLocaleString("en-US")}.`
            : "Shoppers find it under the bell, and see it pop up if they're on the store. Those who turned off news and offers don't get it."}
        </SheetDescription>
      </SheetHeader>

      <div className="grid flex-1 gap-8 p-4 lg:grid-cols-[minmax(0,1fr)_22rem]">
        <div className="grid content-start gap-8">
          <NotificationContentFields
            id="campaign"
            draft={draft}
            setDraft={setDraft}
            issues={issues}
            showAllIssues={attempted}
            variables={[]}
            context={context}
            category="marketing"
            disabled={readOnly || saving}
            uploadsAvailable={uploadsAvailable}
          />

          {!readOnly && (
            <FieldSet>
              <FieldLegend variant="label">Who gets it</FieldLegend>
              <Tabs value={audience} onValueChange={(value: Audience) => setAudience(value)}>
                <TabsList>
                  <TabsTrigger value="all">All shoppers</TabsTrigger>
                  <TabsTrigger value="list">A list</TabsTrigger>
                </TabsList>
                <TabsContent value="all">
                  <p className="text-muted-foreground">
                    Every shopper in this environment, except locked accounts.
                  </p>
                </TabsContent>
                <TabsContent value="list" className="grid gap-3">
                  {campaign?.audience === "list" && (
                    <p className="text-muted-foreground">
                      {`It goes to ${campaignAudiencePhrase(campaign)}. Leave the box empty to keep that list, or add a new one to replace it.`}
                    </p>
                  )}
                  <Field data-invalid={attempted && listIssue ? true : undefined}>
                    <FieldLabel htmlFor="campaign-list">Shoppers</FieldLabel>
                    <Textarea
                      id="campaign-list"
                      rows={5}
                      className="font-mono text-xs"
                      placeholder={"ada@example.com\nyour-user-id-42\n01J9Z3Q4…"}
                      value={listText}
                      disabled={saving}
                      aria-invalid={attempted && listIssue ? true : undefined}
                      onChange={(e) => setListText(e.target.value)}
                    />
                    <FieldDescription>
                      One per line or comma-separated: a shopper's email, your external id or their
                      Loot OS id. A CSV with an email or id column works too.
                    </FieldDescription>
                    {attempted && listIssue && <FieldError>{listIssue}</FieldError>}
                  </Field>
                  <div className="flex flex-wrap items-center gap-2">
                    <Input
                      type="file"
                      accept=".csv,.txt,text/csv,text/plain"
                      aria-label="Upload a CSV or text file"
                      className="h-auto w-auto max-w-full py-1.5"
                      disabled={saving}
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        e.target.value = "";
                        void readList(file);
                      }}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      disabled={
                        list.identifiers.length === 0 ||
                        list.identifiers.length > RECIPIENT_LIST_MAX ||
                        resolve.isPending
                      }
                      onClick={() => resolve.mutate({ identifiers: list.identifiers })}
                    >
                      {resolve.isPending ? "Checking…" : "Check the list"}
                    </Button>
                  </div>
                  <p className="text-muted-foreground">
                    {`${list.identifiers.length.toLocaleString("en-US")} ${list.identifiers.length === 1 ? "entry" : "entries"}`}
                    {list.duplicates > 0 &&
                      `, ${list.duplicates.toLocaleString("en-US")} repeated ${list.duplicates === 1 ? "entry" : "entries"} left out`}
                    .
                  </p>
                  {checked && <ListCheck matched={checked.matched} unmatched={checked.unmatched} />}
                </TabsContent>
              </Tabs>
            </FieldSet>
          )}

          {!readOnly && (
            <FieldSet>
              <FieldLegend variant="label">When</FieldLegend>
              <Tabs value={when} onValueChange={(value: "now" | "later") => setWhen(value)}>
                <TabsList>
                  <TabsTrigger value="now">Send now</TabsTrigger>
                  <TabsTrigger value="later">Schedule</TabsTrigger>
                </TabsList>
                <TabsContent value="now">
                  <p className="text-muted-foreground">
                    It goes out within a minute of confirming.
                  </p>
                </TabsContent>
                <TabsContent value="later" className="grid gap-3">
                  <DayTimeFields
                    id="campaign"
                    value={schedule}
                    disabled={saving}
                    notBefore={now}
                    onChange={setSchedule}
                  />
                  {timeIssue ? (
                    <FieldError>{timeIssue}</FieldError>
                  ) : (
                    <FieldDescription>
                      {`In your time zone, ${Intl.DateTimeFormat().resolvedOptions().timeZone}. Until then you can change it or delete it.`}
                    </FieldDescription>
                  )}
                </TabsContent>
              </Tabs>
            </FieldSet>
          )}
        </div>

        <div className="grid content-start gap-2 lg:sticky lg:top-4 lg:self-start">
          <p className="text-sm font-medium">Preview</p>
          <NotificationPreview category="marketing" rendered={rendered} theme={theme} now={now} />
          <p className="text-xs text-muted-foreground">In this store's words and palette.</p>
        </div>
      </div>

      {!readOnly && (
        <SheetFooter className="sticky bottom-0 flex-row flex-wrap items-center gap-2 border-t bg-popover">
          <Button type="submit" disabled={saving}>
            {saving
              ? "Saving…"
              : when === "now"
                ? "Send now"
                : campaign
                  ? "Save changes"
                  : "Schedule"}
          </Button>
          {campaign && (
            <DeleteNotificationCampaignButton campaignId={campaign.id} onDeleted={onDone} />
          )}
          <p className="text-xs text-muted-foreground sm:ml-auto">
            {`A test goes to ${session.data?.user.email ?? "you"} on this storefront.`}
          </p>
          <Button type="button" variant="outline" disabled={test.isPending} onClick={sendTest}>
            {test.isPending ? "Sending…" : "Send test"}
          </Button>
        </SheetFooter>
      )}

      <AlertDialog open={confirming} onOpenChange={setConfirming}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Send it now?</AlertDialogTitle>
            <AlertDialogDescription>
              {audience === "all"
                ? "It goes to every shopper in this environment within a minute, except locked accounts and those who turned off news and offers."
                : `It goes to ${checked ? `the ${checked.matched.toLocaleString("en-US")} on the list who match a shopper` : "everyone on the list who matches a shopper"} within a minute, except those who turned off news and offers.`}{" "}
              A sent notification can't be taken back.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={saving}
              onClick={() => {
                setConfirming(false);
                persist();
              }}
            >
              Send now
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </form>
  );
}

/** What a list check found: how many entries match a shopper, and the ones that match no one. */
function ListCheck({ matched, unmatched }: { matched: number; unmatched: string[] }) {
  return (
    <div className="grid gap-2 rounded-lg border p-3">
      <p className="flex items-center gap-1.5">
        <CircleCheck aria-hidden className="size-4 text-primary" />
        {`${matched.toLocaleString("en-US")} ${matched === 1 ? "entry matches a shopper" : "entries match a shopper"}.`}
      </p>
      {unmatched.length > 0 && (
        <div className="grid gap-1">
          <p className="text-muted-foreground">
            {`${unmatched.length.toLocaleString("en-US")} ${unmatched.length === 1 ? "matches" : "match"} no one and will be skipped:`}
          </p>
          <ul className="max-h-32 overflow-y-auto font-mono text-xs text-muted-foreground">
            {unmatched.slice(0, UNMATCHED_SHOWN).map((entry) => (
              <li key={entry} className="truncate">
                {entry}
              </li>
            ))}
          </ul>
          {unmatched.length > UNMATCHED_SHOWN && (
            <p className="text-xs text-muted-foreground">
              {`and ${(unmatched.length - UNMATCHED_SHOWN).toLocaleString("en-US")} more.`}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
